<template>
  <div class="posts-management">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-post-outline</v-icon>
                Posts Management
                <span class="page-inline-subtitle">Manage and monitor all content posts with AI moderation insights</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn 
                  color="primary" 
                  @click="createPostDialog = true"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Create Post
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Posts Table -->
      <v-row>
        <v-col cols="12">
          <v-card class="posts-table-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="filters-header-container">
                <div class="filters-single-row">
                  <!-- Title Section -->
                  <div class="table-title-section">
                    <h2 class="table-title">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      All Posts
                    </h2>
                    <div class="results-count" v-if="totalPosts > 0">
                      <div class="count-display">
                        <span class="count-number">{{ totalPosts }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Status Filter Chips Section -->
                  <div class="filters-section">
                    <v-chip-group v-model="selectedStatus" class="status-chips">
                      <v-chip
                        v-for="status in postStatuses"
                        :key="status.value"
                        :color="status.color"
                        :value="status.value"
                        filter
                        outlined
                        small
                        class="modern-chip"
                      >
                        {{ status.text }}
                      </v-chip>
                    </v-chip-group>
                    
                    <div class="filter-actions">
                      <v-btn
                        @click="resetFilters()"
                        outlined
                        class="filter-reset-btn clean-btn"
                        small
                        color="#64748b"
                      >
                        <v-icon small left>mdi-refresh</v-icon>
                        Reset
                      </v-btn>
                      
                      <v-btn
                        v-if="posts.length > 0"
                        color="#10b981"
                        @click="exportPosts()"
                        :loading="isExporting"
                        class="filter-export-btn clean-btn"
                        small
                      >
                        <v-icon small left>mdi-download</v-icon>
                        Export
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-title>

            <v-card-text class="pa-0">
              <v-data-table
                :headers="headers"
                :items="posts"
                :loading="loading"
                :server-items-length="totalPosts"
                :options.sync="options"
                @update:options="fetchPosts"
                class="modern-data-table"
                hide-default-footer
              >
              <template v-slot:[`item.sr_no`]="{ index }">
                <span class="serial-number">{{ (options.page - 1) * options.itemsPerPage + index + 1 }}</span>
              </template>

              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  dark
                  small
                >
                  {{ item.status.toUpperCase() }}
                </v-chip>
              </template>

              <template v-slot:[`item.moderation_result`]="{ item }">
                <div v-if="item.moderation_result">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                        :color="getRiskColor(item.moderation_result.overall_score)"
                        dark
                        small
                        v-bind="attrs"
                        v-on="on"
                      >
                        Risk: {{ (item.moderation_result.overall_score * 100).toFixed(0) }}%
                      </v-chip>
                    </template>
                    <div>
                      <div>Toxicity: {{ (item.moderation_result.toxicity_score * 100).toFixed(1) }}%</div>
                      <div>Spam: {{ (item.moderation_result.spam_score * 100).toFixed(1) }}%</div>
                      <div>Inappropriate: {{ (item.moderation_result.inappropriate_score * 100).toFixed(1) }}%</div>
                      <div>AI Recommendation: {{ item.moderation_result.ai_recommendation }}</div>
                    </div>
                  </v-tooltip>
                </div>
                <v-chip v-else color="grey" dark small>
                  Pending Analysis
                </v-chip>
              </template>

              <template v-slot:[`item.user.first_name`]="{ item }">
                {{ item.user.first_name }} {{ item.user.last_name }}
              </template>

              <template v-slot:[`item.created_at`]="{ item }">
                {{ formatDate(item.created_at) }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon
                  small
                  @click="viewPost(item)"
                  color="info"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  v-if="item.status === 'pending' || item.status === 'rejected'"
                  icon
                  small
                  @click="editPost(item)"
                  color="primary"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click="deletePost(item)"
                  color="error"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              </v-data-table>
              
              <!-- Custom Pagination -->
              <div class="modern-pagination pa-4">
                <v-pagination
                  v-model="options.page"
                  :length="Math.ceil(totalPosts / options.itemsPerPage)"
                  :total-visible="7"
                  color="primary"
                  @input="fetchPosts"
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Create/Edit Post Dialog -->
    <v-dialog v-model="createPostDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          {{ editingPost ? 'Edit Post' : 'Create New Post' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="postForm" v-model="postFormValid">
            <v-text-field
              v-model="postForm.title"
              label="Title"
              :rules="[rules.required]"
              required
            ></v-text-field>
            
            <v-select
              v-model="postForm.type"
              :items="postTypes"
              label="Content Type"
              :rules="[rules.required]"
              required
            ></v-select>

            <v-textarea
              v-model="postForm.content"
              label="Content"
              :rules="[rules.required]"
              rows="6"
              required
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closePostDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!postFormValid"
            @click="savePost"
          >
            {{ editingPost ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Post Dialog -->
    <v-dialog v-model="viewPostDialog" max-width="800px">
      <v-card v-if="selectedPost">
        <v-card-title>
          {{ selectedPost.title }}
          <v-spacer></v-spacer>
          <v-chip
            :color="getStatusColor(selectedPost.status)"
            dark
          >
            {{ selectedPost.status.toUpperCase() }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="8">
              <h4>Content:</h4>
              <p>{{ selectedPost.content }}</p>
              
              <div class="mt-4">
                <strong>Author:</strong> {{ selectedPost.user.first_name }} {{ selectedPost.user.last_name }}<br>
                <strong>Type:</strong> {{ selectedPost.type }}<br>
                <strong>Created:</strong> {{ formatDate(selectedPost.created_at) }}
              </div>
            </v-col>
            <v-col cols="4" v-if="selectedPost.moderation_result">
              <v-card outlined>
                <v-card-title class="text-h6">AI Analysis</v-card-title>
                <v-card-text>
                  <div class="mb-2">
                    <strong>Overall Risk:</strong>
                    <v-progress-linear
                      :value="selectedPost.moderation_result.overall_score * 100"
                      :color="getRiskColor(selectedPost.moderation_result.overall_score)"
                      height="20"
                      class="mt-1"
                    >
                      {{ (selectedPost.moderation_result.overall_score * 100).toFixed(1) }}%
                    </v-progress-linear>
                  </div>
                  
                  <div class="mb-2">
                    <strong>Toxicity:</strong>
                    <v-progress-linear
                      :value="selectedPost.moderation_result.toxicity_score * 100"
                      color="red"
                      height="15"
                      class="mt-1"
                    >
                      {{ (selectedPost.moderation_result.toxicity_score * 100).toFixed(1) }}%
                    </v-progress-linear>
                  </div>
                  
                  <div class="mb-2">
                    <strong>Spam:</strong>
                    <v-progress-linear
                      :value="selectedPost.moderation_result.spam_score * 100"
                      color="orange"
                      height="15"
                      class="mt-1"
                    >
                      {{ (selectedPost.moderation_result.spam_score * 100).toFixed(1) }}%
                    </v-progress-linear>
                  </div>
                  
                  <div class="mb-2">
                    <strong>Inappropriate:</strong>
                    <v-progress-linear
                      :value="selectedPost.moderation_result.inappropriate_score * 100"
                      color="purple"
                      height="15"
                      class="mt-1"
                    >
                      {{ (selectedPost.moderation_result.inappropriate_score * 100).toFixed(1) }}%
                    </v-progress-linear>
                  </div>

                  <div class="mt-3">
                    <strong>AI Recommendation:</strong>
                    <v-chip
                      :color="getRecommendationColor(selectedPost.moderation_result.ai_recommendation)"
                      dark
                      small
                    >
                      {{ selectedPost.moderation_result.ai_recommendation.toUpperCase() }}
                    </v-chip>
                  </div>

                  <div v-if="selectedPost.moderation_result.ai_reasoning" class="mt-3">
                    <strong>AI Reasoning:</strong>
                    <p class="text-caption">{{ selectedPost.moderation_result.ai_reasoning }}</p>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="viewPostDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "PostsIndex",
  data() {
    return {
      loading: false,
      submitting: false,
      posts: [],
      totalPosts: 0,
      selectedStatus: null,
      createPostDialog: false,
      viewPostDialog: false,
      editingPost: null,
      selectedPost: null,
      postFormValid: false,
      postForm: {
        title: '',
        content: '',
        type: 'text'
      },
      options: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ['created_at'],
        sortDesc: [true]
      },
      headers: [
        { text: 'Sr. No.', value: 'sr_no', sortable: false, width: '80px' },
        { text: 'Title', value: 'title', sortable: true, width: '200px' },
        { text: 'Author', value: 'user.first_name', sortable: false, width: '150px' },
        { text: 'Type', value: 'type', sortable: true, width: '100px' },
        { text: 'Status', value: 'status', sortable: true, width: '120px' },
        { text: 'AI Analysis', value: 'moderation_result', sortable: false, width: '150px' },
        { text: 'Created', value: 'created_at', sortable: true, width: '140px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' },
      ],
      isExporting: false,
      postStatuses: [
        { text: 'All', value: null, color: 'grey' },
        { text: 'Pending', value: 'pending', color: 'orange' },
        { text: 'Approved', value: 'approved', color: 'green' },
        { text: 'Rejected', value: 'rejected', color: 'red' },
        { text: 'Flagged', value: 'flagged', color: 'purple' },
      ],
      postTypes: [
        { text: 'Text', value: 'text' },
        { text: 'Image', value: 'image' },
        { text: 'Video', value: 'video' },
      ],
      rules: {
        required: value => !!value || 'Required field'
      }
    };
  },
  watch: {
    selectedStatus() {
      this.options.page = 1;
      this.fetchPosts();
    }
  },
  mounted() {
    this.fetchPosts();
  },
  methods: {
    async fetchPosts() {
      this.loading = true;
      try {
        const params = {
          page: this.options.page,
          per_page: this.options.itemsPerPage
        };

        if (this.selectedStatus) {
          params.status = this.selectedStatus;
        }

        const response = await this.$http.get('posts', { params });
        
        this.posts = response.data.data.data;
        this.totalPosts = response.data.data.total;
      } catch (error) {
        this.$store.dispatch('errors/setErrors', error.response?.data?.errors || {});
      } finally {
        this.loading = false;
      }
    },

    async savePost() {
      if (!this.$refs.postForm.validate()) return;

      this.submitting = true;
      try {
        if (this.editingPost) {
          await this.$http.patch(`posts/${this.editingPost.id}`, this.postForm);
        } else {
          await this.$http.post('posts', this.postForm);
        }

        this.closePostDialog();
        this.fetchPosts();
        this.$store.dispatch('errors/setErrors', {});
      } catch (error) {
        this.$store.dispatch('errors/setErrors', error.response?.data?.errors || {});
      } finally {
        this.submitting = false;
      }
    },

    editPost(post) {
      this.editingPost = post;
      this.postForm = {
        title: post.title,
        content: post.content,
        type: post.type
      };
      this.createPostDialog = true;
    },

    viewPost(post) {
      this.selectedPost = post;
      this.viewPostDialog = true;
    },

    async deletePost(post) {
      if (confirm('Are you sure you want to delete this post?')) {
        try {
          await this.$http.delete(`posts/${post.id}`);
          this.fetchPosts();
        } catch (error) {
          this.$store.dispatch('errors/setErrors', error.response?.data?.errors || {});
        }
      }
    },

    closePostDialog() {
      this.createPostDialog = false;
      this.editingPost = null;
      this.postForm = {
        title: '',
        content: '',
        type: 'text'
      };
      this.$refs.postForm?.reset();
    },

    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        approved: 'green',
        rejected: 'red',
        flagged: 'purple'
      };
      return colors[status] || 'grey';
    },

    getRiskColor(score) {
      if (score >= 0.7) return 'red';
      if (score >= 0.4) return 'orange';
      return 'green';
    },

    getRecommendationColor(recommendation) {
      const colors = {
        approve: 'green',
        reject: 'red',
        flag: 'purple'
      };
      return colors[recommendation] || 'grey';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString();
    },

    resetFilters() {
      this.selectedStatus = null;
      this.options.page = 1;
      this.fetchPosts();
    },

    async exportPosts() {
      this.isExporting = true;
      try {
        // TODO: Implement export functionality
        console.log('Export posts functionality to be implemented');
      } catch (error) {
        console.error('Export failed:', error);
      } finally {
        this.isExporting = false;
      }
    }
  }
};
</script>

<style scoped>
.posts-management {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-inline-subtitle {
  font-size: 16px;
  color: #64748b;
  font-weight: 400;
  margin-left: 16px;
  display: inline;
}

.page-title-section {
  flex: 1;
  width: 100%;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #667eea 50%, #764ba2 75%, #f093fb 100%) !important;
  -webkit-background-clip: text !important;
  background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  font-weight: 900 !important;
  filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
  animation: pulse-glow 3s ease-in-out infinite;
  font-size: 32px !important;
}

@keyframes pulse-glow {
  0%, 100% { 
    filter: drop-shadow(0 2px 6px rgba(102, 126, 234, 0.4)) drop-shadow(0 0 12px rgba(255, 107, 107, 0.3));
    transform: scale(1);
  }
  50% { 
    filter: drop-shadow(0 4px 12px rgba(102, 126, 234, 0.6)) drop-shadow(0 0 20px rgba(255, 107, 107, 0.5));
    transform: scale(1.05);
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
  justify-self: flex-end;
  margin-left: auto;
}

/* Posts Table Card */
.posts-table-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  background: white;
  overflow: hidden;
}

.modern-card-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding: 24px !important;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

/* Filters Header Container */
.filters-header-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.filters-single-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 24px;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.results-count {
  display: flex;
  align-items: center;
}

.count-display {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 0 16px;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.count-number {
  font-size: 18px;
  font-weight: 700;
  color: white;
  line-height: 1;
}

/* Filters Section */
.filters-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
}

.status-chips {
  display: flex;
  gap: 8px;
}

.modern-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
  transition: all 0.3s ease;
}

.modern-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Filter Actions */
.filter-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
}

/* Clean Buttons */
.clean-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  height: 40px !important;
  min-width: auto !important;
  transition: all 0.2s ease;
  font-size: 13px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

.clean-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
}

/* Reset Button */
.filter-reset-btn.clean-btn {
  background: white !important;
  border: 1.5px solid #e2e8f0 !important;
  color: #64748b !important;
}

.filter-reset-btn.clean-btn:hover {
  border-color: #cbd5e1 !important;
  background: #f8fafc !important;
}

/* Export Button */
.filter-export-btn.clean-btn {
  background: #10b981 !important;
  color: white !important;
  border: none !important;
}

.filter-export-btn.clean-btn:hover {
  background: #059669 !important;
}

/* Header Button Styling */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  padding: 0 20px !important;
  height: 44px !important;
  font-size: 14px !important;
  transition: all 0.3s ease;
  min-width: auto !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
}

.modern-btn:before {
  background-color: transparent;
}

/* Data Table */
.modern-data-table {
  background: transparent !important;
}

.modern-data-table >>> .v-data-table__wrapper {
  background: white;
}

.modern-data-table >>> thead tr th {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border: none !important;
  border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
  padding: 16px 12px !important;
}

.modern-data-table >>> thead tr th:last-child {
  border-right: none !important;
}

.modern-data-table >>> tbody tr {
  transition: all 0.3s ease;
}

.modern-data-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
}

.modern-data-table >>> tbody tr td {
  padding: 16px 12px !important;
  border-bottom: 1px solid rgba(102, 126, 234, 0.1) !important;
  border-right: 1px solid rgba(102, 126, 234, 0.1) !important;
}

.modern-data-table >>> tbody tr td:last-child {
  border-right: none !important;
}

/* Serial Number Styling */
.serial-number {
  font-weight: 600;
  font-size: 14px;
  color: #64748b;
  background: rgba(102, 126, 234, 0.08);
  padding: 4px 8px;
  border-radius: 6px;
  display: inline-block;
  min-width: 24px;
  text-align: center;
}

/* Pagination */
.modern-pagination {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid rgba(102, 126, 234, 0.1);
  display: flex;
  justify-content: center;
}

.modern-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  font-weight: 500;
}

.modern-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
}

/* Action Buttons in Table */
.v-btn--icon {
  border-radius: 8px !important;
}

/* Chips in table */
.v-chip {
  border-radius: 8px !important;
  font-weight: 500 !important;
}

/* Progress Indicators */
.v-progress-linear {
  border-radius: 4px !important;
}

/* Dialogs */
.v-dialog >>> .v-card {
  border-radius: 16px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.v-dialog >>> .v-card-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white !important;
  font-weight: 600;
  padding: 20px 24px !important;
}

.v-dialog >>> .v-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.v-dialog >>> .v-card-text {
  padding: 24px !important;
}

.v-dialog >>> .v-card-actions {
  padding: 16px 24px 24px 24px !important;
}

/* Form Elements in Dialogs */
.v-dialog >>> .v-text-field--outlined .v-input__control .v-input__slot {
  border-radius: 12px !important;
}

.v-dialog >>> .v-textarea textarea {
  border-radius: 12px !important;
}

.v-dialog >>> .v-select--outlined .v-input__control .v-input__slot {
  border-radius: 12px !important;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .filters-single-row {
    gap: 16px;
  }
}

@media (max-width: 960px) {
  .filters-single-row {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .table-title-section {
    justify-content: center;
  }
  
  .filters-section {
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .filter-actions {
    justify-content: center;
  }
  
  .page-inline-subtitle {
    display: block;
    margin-left: 0;
    margin-top: 4px;
    font-size: 14px;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .page-title {
    font-size: 22px;
  }
  
  .page-header {
    padding: 14px 18px;
  }
}

@media (max-width: 600px) {
  .filters-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .clean-btn {
    width: 100%;
  }
  
  .page-header {
    padding: 12px 16px;
  }
  
  .page-title {
    font-size: 20px;
  }
  
  .modern-card-title {
    padding: 16px !important;
    flex-direction: column;
    align-items: stretch;
  }
  
  .table-title {
    font-size: 18px;
    text-align: center;
    margin-bottom: 16px;
  }
  
  .v-dialog >>> .v-card {
    margin: 16px !important;
  }
}
</style>